import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';

const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const GRADIENT = `linear-gradient(135deg, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`;

// Use direct fetch instead of api service since user may not be authenticated
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [subscription, setSubscription] = useState(null);
  const navigate = useNavigate();
  
  const sessionId = searchParams.get('session_id');

  const checkPayment = useCallback(async () => {
    if (!sessionId) return;
    
    try {
      // Use the public verify endpoint that doesn't require authentication
      const response = await fetch(`${API_URL}/api/subscription/verify/${sessionId}`);
      const data = await response.json();
      
      if (response.ok && data.status === 'paid') {
        setStatus('success');
        setSubscription(data);
      } else if (data.status === 'unpaid' || data.status === 'no_payment_required') {
        setStatus('pending');
      } else {
        setStatus('pending');
      }
    } catch (error) {
      setStatus('error');
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      checkPayment();
    } else {
      setStatus('error');
    }
  }, [sessionId, checkPayment]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#22C55E] mx-auto mb-4" />
          <p className="text-zinc-400">Verificando pagamento...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">❌</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Erro ao verificar pagamento</h1>
          <p className="text-zinc-400 mb-6">
            Não foi possível verificar o status do pagamento. Entre em contato com o suporte.
          </p>
          <Button onClick={() => navigate('/assinatura')} className="bg-white/10 text-white">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <Loader2 className="w-12 h-12 animate-spin text-[#22C55E] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-4">Aguardando confirmação</h1>
          <p className="text-zinc-400 mb-6">
            Seu pagamento está sendo processado. Isso pode levar alguns minutos.
          </p>
          <Button onClick={checkPayment} className="bg-white/10 text-white">
            Verificar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        {/* Success animation */}
        <div className="relative w-24 h-24 mx-auto mb-8">
          <div 
            className="absolute inset-0 rounded-full animate-ping"
            style={{ background: `${PRIMARY_COLOR}20` }}
          />
          <div 
            className="absolute inset-0 rounded-full flex items-center justify-center"
            style={{ background: `${PRIMARY_COLOR}20` }}
          >
            <CheckCircle className="w-12 h-12" style={{ color: PRIMARY_COLOR }} />
          </div>
        </div>

        <h1 
          className="text-3xl font-bold mb-4 bg-clip-text text-transparent"
          style={{ backgroundImage: GRADIENT }}
        >
          Pagamento Confirmado!
        </h1>
        
        <p className="text-zinc-400 text-lg mb-8">
          Sua assinatura foi ativada com sucesso. Agora você tem acesso completo ao ALVO.
        </p>

        {subscription?.expires_at && (
          <div className="bg-white/5 rounded-xl p-4 mb-8">
            <p className="text-zinc-500 text-sm">Válido até</p>
            <p className="text-white font-bold text-lg">
              {new Date(subscription.expires_at).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>
        )}

        <Button
          onClick={() => navigate('/dashboard')}
          className="h-12 px-8 text-black font-semibold"
          style={{ background: GRADIENT }}
        >
          Acessar o Sistema
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

export const PaymentCancelledPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">😕</span>
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-4">
          Pagamento Cancelado
        </h1>
        
        <p className="text-zinc-400 mb-8">
          O processo de pagamento foi cancelado. Você pode tentar novamente quando quiser.
        </p>

        <div className="space-y-3">
          <Button
            onClick={() => navigate('/assinatura')}
            className="w-full h-12 text-black font-semibold"
            style={{ background: GRADIENT }}
          >
            Tentar Novamente
          </Button>
          
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="w-full h-12 border-zinc-700 text-zinc-300 hover:bg-white/5"
          >
            Voltar ao Início
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
